import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Loader, MoreVertical, UserPlus } from "lucide-react";
import { useState } from "react";
import { useQuery } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useModalState from "@/hooks/useModalState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import * as z from "zod";
import { toast } from "sonner";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

function UserForm({ isOpen, setIsOpen, refetch, record }) {
  const { t } = useTranslation();

  const formSchema = z.object({
    name: z.string().min(2, t("pages.systemUsers.form.nameMin") || "Name must be at least 2 characters"),
    first_name: z.string().min(2, t("pages.systemUsers.form.firstNameMin") || "First name must be at least 2 characters"),
    last_name: z.string().min(2, t("pages.systemUsers.form.lastNameMin") || "Last name must be at least 2 characters"),
    email: z.string().email(t("pages.systemUsers.form.emailInvalid") || "Invalid email address"),
    password: z.string().min(6, t("pages.systemUsers.form.passwordMin") || "Password must be at least 6 characters").optional(),
    phone: z.string().min(10, t("pages.systemUsers.form.phoneMin") || "Phone number must be at least 10 characters").optional(),
    role: z.string().min(1, t("pages.systemUsers.form.roleRequired") || "Role is required"),
    branchId: z.string().optional().nullable(),
    groupId: z.string().optional().nullable(),
    status: z.enum(["active", "inactive"]),
    isAdmin: z.boolean().default(false),
    profileUrl: z.string().optional(),
  }).superRefine((data, ctx) => {
    // If not admin, branchId and groupId are required
    if (!data.isAdmin) {
      if (!data.branchId || data.branchId.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("pages.systemUsers.form.branchRequired") || "Branch is required for non-admin users",
          path: ["branchId"],
        });
      }
      if (!data.groupId || data.groupId.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("pages.systemUsers.form.groupRequired") || "Group is required for non-admin users",
          path: ["groupId"],
        });
      }
    }
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: record
      ? {
        ...record,
        role: record.role?.toString() || (record.role?.id?.toString() || ""),
        branchId: record.isAdmin ? undefined : (record.branchId?.toString() || (record.branch?.id?.toString() || "")),
        groupId: record.isAdmin ? undefined : (record.groupId?.toString() || (record.group?.id?.toString() || "")),
        password: ""
      }
      : {
        name: "",
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        phone: "",
        role: "",
        branchId: undefined,
        groupId: undefined,
        status: "active",
        isAdmin: false,
        profileUrl: "",
      },
  });

  const watchBranchId = form.watch("branchId");
  const watchIsAdmin = form.watch("isAdmin");

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Prepare submit data
    const submitData: any = {
      ...values,
      role: Number(values.role),
      isAdmin: Boolean(values.isAdmin),
    };

    // Handle password for updates
    if (record && !submitData.password) {
      delete submitData.password;
    }

    // Handle branch and group based on admin status
    if (values.isAdmin) {
      submitData.branchId = null;
      submitData.groupId = null;
    } else {
      // Convert to numbers for non-admin users
      submitData.branchId = submitData.branchId ? Number(submitData.branchId) : null;
      submitData.groupId = submitData.groupId ? Number(submitData.groupId) : null;
    }

    console.log('Submitting user data:', submitData);

    const q = record
      ? api.patch(`/users/${record.id}`, submitData)
      : api.post("/users", submitData);

    try {
      await q;
      refetch();
      toast.success(
        record ? t("pages.systemUsers.form.updateSuccess") : t("pages.systemUsers.form.createSuccess")
      );
      setIsOpen(false);
      form.reset();
    } catch (e) {
      console.error('User creation error:', e);
      toast.error(e.response?.data?.message || e.message);
      const errors = e?.response?.data?.meta?.errors || {};
      Object.keys(errors)?.forEach((field: any) => {
        form.setError(field, {
          message: errors[field],
        });
      });
    }
  }

  const { data: roles } = useQuery(["roles"], async () => {
    const { data } = await api.get("/roles");
    return data.results;
  });

  const { data: branches } = useQuery(["branches"], async () => {
    const { data } = await api.get("/branches");
    return data.results;
  });

  const { data: groups } = useQuery(
    ["groups", { branchId: watchBranchId }],
    async () => {
      if (!watchBranchId) return [];
      const { data } = await api.get("/groups", {
        params: {
          filters: [
            {
              field: "branchId",
              operator: "eq",
              value: watchBranchId,
            },
          ],
        },
      });
      return data.results;
    },
    {
      enabled: Boolean(watchBranchId),
    }
  );


  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg p-0 gap-0">
        <DialogHeader className="border-b px-4 py-2.5">
          <DialogTitle className="text-[15px]">
            {record ? t("pages.systemUsers.form.updateTitle") : t("pages.systemUsers.form.addTitle")}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[80vh]">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 py-4 px-4"
            >

              {/* User Information Section */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  {t("pages.systemUsers.form.userInformation")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t("pages.systemUsers.form.firstName")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("pages.systemUsers.form.firstNamePlaceholder")}
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t("pages.systemUsers.form.lastName")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("pages.systemUsers.form.lastNamePlaceholder")}
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>{t("pages.systemUsers.form.fullName")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("pages.systemUsers.form.fullNamePlaceholder")}
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  {t("pages.systemUsers.form.contactInformation")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t("pages.systemUsers.form.email")}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t("pages.systemUsers.form.emailPlaceholder")}
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t("pages.systemUsers.form.phone")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("pages.systemUsers.form.phonePlaceholder")}
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Account Settings */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  {t("pages.systemUsers.form.accountSettings")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{record ? t("pages.systemUsers.form.newPassword") : t("pages.systemUsers.form.password")}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder={record ? t("pages.systemUsers.form.passwordPlaceholderUpdate") : t("pages.systemUsers.form.passwordPlaceholder")}
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t("pages.systemUsers.form.status")}</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message}>
                                <SelectValue placeholder={t("pages.systemUsers.form.statusPlaceholder")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">{t("pages.systemUsers.form.active")}</SelectItem>
                              <SelectItem value="inactive">{t("pages.systemUsers.form.inactive")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Permissions and Assignment */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  {t("pages.systemUsers.form.permissionsAssignment")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t("pages.systemUsers.form.role")}</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message}>
                                <SelectValue placeholder={t("pages.systemUsers.form.rolePlaceholder")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {roles?.map((role) => (
                                <SelectItem
                                  key={role.id}
                                  value={role.id?.toString()}
                                >
                                  {role.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isAdmin"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-end space-x-2 space-y-0 rounded-md pt-6">
                        <FormControl>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e);
                              // Clear branch and group when admin is checked
                              if (e.target.checked) {
                                form.setValue("branchId", undefined);
                                form.setValue("groupId", undefined);
                                // Clear any validation errors for these fields
                                form.clearErrors("branchId");
                                form.clearErrors("groupId");
                              }
                            }}
                            id="is-admin"
                          />
                        </FormControl>
                        <FormLabel htmlFor="is-admin" className="text-sm font-normal">
                          {t("pages.systemUsers.form.administratorAccess")}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  {!watchIsAdmin && (
                    <>
                      <FormField
                        control={form.control}
                        name="branchId"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel>{t("pages.systemUsers.form.branch")}</FormLabel>
                            <FormControl>
                              <Select
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  form.setValue("groupId", undefined);
                                }}
                                value={field.value || ""}
                              >
                                <FormControl>
                                  <SelectTrigger error={fieldState?.error?.message}>
                                    <SelectValue placeholder={t("pages.systemUsers.form.branchPlaceholder")} />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {branches?.map((branch) => (
                                    <SelectItem
                                      key={branch.id}
                                      value={branch.id?.toString()}
                                    >
                                      {branch.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="groupId"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel>{t("pages.systemUsers.form.group")}</FormLabel>
                            <FormControl>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                                disabled={!watchBranchId}
                              >
                                <FormControl>
                                  <SelectTrigger error={fieldState?.error?.message}>
                                    <SelectValue placeholder={watchBranchId ? t("pages.systemUsers.form.groupPlaceholder") : t("pages.systemUsers.form.groupPlaceholderDisabled")} />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {groups?.map((group) => (
                                    <SelectItem
                                      key={group.id}
                                      value={group.id?.toString()}
                                    >
                                      {group.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                  {watchIsAdmin && (
                    <div className="col-span-2 text-sm text-gray-500 italic">
                      {t("pages.systemUsers.form.adminNote")}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </ScrollArea>

        <DialogFooter className="border-t px-4 py-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              form.reset();
            }}
          >
            {t("pages.systemUsers.form.cancel")}
          </Button>
          <Button
            disabled={form.formState.isSubmitting}
            type="submit"
            size="sm"
            onClick={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitting && (
              <Loader className="mr-2 h-4 w-4 text-white animate-spin" />
            )}
            {record ? t("pages.systemUsers.form.updateButton") : t("pages.systemUsers.form.addButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function SystemUsers() {
  const { t } = useTranslation();
  const [recordToEdit, setRecordToEdit] = useState(undefined);

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t("pages.systemUsers.table.selectAll")}
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t("pages.systemUsers.table.selectRow")}
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.systemUsers.table.user")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={row.original.profileUrl} />
              <AvatarFallback>{row.original.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <a href="#" className="font-medium hover:underline">
                {row.original.name}
              </a>
              <span className="text-xs text-gray-500">
                {row.original.first_name} {row.original.last_name}
              </span>
            </div>
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "contact",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.systemUsers.table.contact")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <span className="text-sm">{row.original.email}</span>
            <span className="text-xs text-gray-500">{row.original.phone || "—"}</span>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: true,
    },
    {
      accessorKey: "branch",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.systemUsers.table.branch")} />
      ),
      cell: ({ row }) => {
        const isAdmin = row.original.isAdmin;
        return (
          <div className="flex items-center gap-1">
            {isAdmin && !row.original.branch ? (
              <span className="text-gray-500 italic">{t("pages.systemUsers.table.allBranches")}</span>
            ) : (
              row.original.branch?.name || "—"
            )}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      accessorKey: "group",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.systemUsers.table.group")} />
      ),
      cell: ({ row }) => {
        const isAdmin = row.original.isAdmin;
        return (
          <div className="flex items-center gap-1">
            {isAdmin && !row.original.group ? (
              <span className="text-gray-500 italic">{t("pages.systemUsers.table.allGroups")}</span>
            ) : (
              row.original.group?.name || "—"
            )}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      accessorKey: "role",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.systemUsers.table.role")} />
      ),
      cell: ({ row }) => {
        // Use displayRole if present, otherwise fallback
        const displayRole = row.original.displayRole;
        const role = displayRole || row.original.role?.name || row.getValue("role");
        const isAdmin = row.original.isAdmin;

        return (
          <div className="flex items-center gap-2">
            {role ? (
              <Badge variant="outline" className="font-normal">
                {role}
              </Badge>
            ) : (
              <span>—</span>
            )}
            {isAdmin && (
              <Badge variant="secondary" className="ml-1">
                {t("pages.systemUsers.table.admin")}
              </Badge>
            )}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.systemUsers.table.status")} />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3">
            {row.original?.status && (
              <Badge
                variant={
                  {
                    inactive: "destructive",
                    active: "success",
                  }[row.original?.status]
                }
                className="capitalize"
              >
                {row.original?.status}
              </Badge>
            )}
          </div>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("pages.systemUsers.table.created")} />
      ),
      cell: ({ row }) => {
        // Format date nicely
        const date = new Date(row.original.createdAt);
        const formattedDate = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);

        return <div className="text-sm">{formattedDate}</div>;
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="" />
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-end mt-1 gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-7 w-8 p-0">
                <span className="sr-only">{t("pages.systemUsers.table.openMenu")}</span>
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("pages.systemUsers.table.actions")}</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => {
                  setRecordToEdit(row?.original);
                }}
              >
                {t("pages.systemUsers.actions.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  confirmModal.open({ meta: row?.original });
                }}
                className="text-red-600"
              >
                {t("pages.systemUsers.actions.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const newRecordModal = useModalState();
  const confirmModal = useConfirmModal();

  const recordsQuery = useQuery({
    queryKey: [
      "users",
      {
        search: searchText,
        filter: columnFilters,
        sort: sorting,
        pageIndex,
        pageSize,
      },
    ],
    keepPreviousData: true,
    queryFn: async () => {
      const { data } = await api.get(`/users`, {
        params: {
          page_size: pageSize,
          page: pageIndex + 1,
          ...(searchText && { search: searchText }),
          filters: [
            ...columnFilters.map((e) => {
              return {
                field: e.id,
                operator: "in",
                value: e.value?.map((e) => e?.value || e),
              };
            }),
          ],
        },
      });

      return {
        items: data?.results.map((e) => {
          return {
            ...e,
            role: e.role?.name,
            roleId: e.role?.id,
          };
        }),
        totalPages: data?.totalPages,
      };
    },
  });

  const handleDelete = (record) => {
    confirmModal.setIsLoading(true);
    return api
      .delete(`/users/${record.id}`)
      .then(() => {
        recordsQuery.refetch();
        confirmModal.close();
        toast.success(t("pages.systemUsers.deleteSuccess"));
      })
      .catch((e) => {
        confirmModal.setIsLoading(false);
        toast.error(e.message);
      });
  };

  return (
    <>
      <ConfirmModal
        title={t("pages.systemUsers.deleteConfirm.title")}
        description={t("pages.systemUsers.deleteConfirm.description", { name: confirmModal.meta?.name || t("pages.systemUsers.deleteConfirm.thisUser") })}
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          handleDelete(meta);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <div className="sm:px-2 px-2">
        <div className="flex items-center justify-between space-y-2 my-3">
          <div className="flex items-start gap-2 flex-col">
            <h2 className="text-[16px] font-semibold tracking-tight">
              {t("pages.systemUsers.title")}
            </h2>
          </div>
          <div className="space-x-2">
            <Button
              onClick={() => {
                newRecordModal.open();
              }}
              size="sm"
              className="shadow-sm"
            >
              <UserPlus size={16} className="mr-2" />
              <span>{t("pages.systemUsers.addNew")}</span>
            </Button>
          </div>
        </div>

        <DataTable
          isFetching={recordsQuery.isFetching}
          defaultColumnVisibility={{
            createdAt: false,
          }}
          isLoading={recordsQuery.status === "loading"}
          data={recordsQuery?.data?.items || []}
          columns={columns}
          onSearch={(e) => {
            setSearchText(e);
          }}
          sorting={sorting}
          setSorting={setSorting}
          pageCount={recordsQuery?.data?.totalPages}
          setPagination={setPagination}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setColumnFilters={setColumnFilters}
          columnFilters={columnFilters}
          facets={[
            {
              accessorKey: "status",
              title: t("pages.systemUsers.table.status"),
              options: [
                { label: t("pages.systemUsers.form.active"), value: "active" },
                { label: t("pages.systemUsers.form.inactive"), value: "inactive" },
              ],
            },
            {
              accessorKey: "role",
              title: t("pages.systemUsers.table.role"),
              options: recordsQuery?.data?.items
                ?.map((user) => user.role)
                .filter(Boolean) // Filter out null/undefined values
                .filter((value, index, self) => self.indexOf(value) === index)
                .map((role) => ({
                  label: role,
                  value: role,
                })) || [],
            },
            {
              accessorKey: "branch.name",
              title: t("pages.systemUsers.table.branch"),
              options: recordsQuery?.data?.items
                ?.map((user) => user.branch?.name)
                .filter(Boolean)
                .filter((value, index, self) => self.indexOf(value) === index)
                .map((branch) => ({
                  label: branch,
                  value: branch,
                })) || [],
            },
          ]}
        />
      </div>

      <UserForm
        isOpen={newRecordModal.isOpen || Boolean(recordToEdit)}
        setIsOpen={(e) => {
          newRecordModal.setisOpen(e);
          if (!e) {
            setRecordToEdit(undefined);
          }
        }}
        refetch={recordsQuery.refetch}
        record={recordToEdit}
      />
    </>
  );
}
